import { useEffect, useRef } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AnySchema, object as yupObject } from 'yup'
import {
  ComboboxGroupItem,
  ComboboxGroupProps,
  ComboboxItem,
  ComboboxProps,
  InputProps,
} from '@node-space/ui-components'
import { FieldMaxLength } from 'constants/forms'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useYupFields } from 'hooks/forms/useYupFields'
import { useIndustryOptions } from 'hooks/queries/Customers/useIndustryOptions'
import { useMonthlyVolumesQuery } from 'hooks/queries/Customers/useMonthlyVolumesQuery'
import { useAllowedCountriesQuery } from 'hooks/queries/useAllowedCountriesQuery'
import { CountryCodes, type ErrorDetails } from 'types/types'
import { getCountryOptions } from 'utils/forms'
import {
  BASE_CUSTOMER_OPTIONS,
  BUSINESS_CUSTOMER_LABEL_MAPPING,
  mapBusinessErrorKeyToFormKey,
  setFieldErrors,
} from '../helpers/form'
import { BusinessCustomerFormData, BusinessCustomerFormKey, UseCase } from '../helpers/types'

type BusinessCustomerFormSchema = Partial<Record<BusinessCustomerFormKey, AnySchema>>

const BUSINESS_COMBOBOX_KEYS: BusinessCustomerFormKey[] = [
  'countryOfIncorporation',
  'monthlyExpectedVolumes',
  'useCase',
]

const BUSINESS_COMBOBOX_GROUP_KEYS: BusinessCustomerFormKey[] = ['industry']

type BusinessComboboxKey = (typeof BUSINESS_COMBOBOX_KEYS)[number]
type BusinessComboboxGroupKey = (typeof BUSINESS_COMBOBOX_GROUP_KEYS)[number]
type BusinessInputKey = Exclude<
  BusinessCustomerFormKey,
  BusinessComboboxKey | BusinessComboboxGroupKey
>

type BusinessCustomerFormFields = Partial<
  Record<BusinessInputKey, InputProps> &
    Record<BusinessComboboxKey, ComboboxProps> &
    Record<BusinessComboboxGroupKey, ComboboxGroupProps>
>

const isBusinessComboboxKey = (key: BusinessCustomerFormKey): key is BusinessComboboxKey => {
  return BUSINESS_COMBOBOX_KEYS.includes(key as BusinessComboboxKey)
}

const isBusinessComboboxGroupKey = (
  key: BusinessCustomerFormKey
): key is BusinessComboboxGroupKey => {
  return BUSINESS_COMBOBOX_GROUP_KEYS.includes(key as BusinessComboboxGroupKey)
}

export const useBusinessCustomerForm = (
  defaultValues: BusinessCustomerFormData,
  fieldErrors?: ErrorDetails,
  isReliance?: boolean
) => {
  const { t } = useTranslation()
  const { getConditionalYupField, getRequiredYupField, getYupField } = useYupFields()

  const {
    data: allowedCountries = [],
    isFetching: isFetchingAllowedCountries,
    isError: isErrorFetchingAllowedCountries,
  } = useAllowedCountriesQuery()

  const {
    industryOptions,
    isPending: isFetchingIndustries,
    error: isErrorFetchingIndustries,
  } = useIndustryOptions()

  const {
    data: monthlyVolumes,
    isFetching: isFetchingMonthlyVolumes,
    isError: isErrorFetchingMonthlyVolumes,
  } = useMonthlyVolumesQuery()

  const comboboxItems: Partial<Record<BusinessCustomerFormKey, ComboboxItem[]>> = {
    ...BASE_CUSTOMER_OPTIONS,
    countryOfIncorporation: getCountryOptions(allowedCountries),
    monthlyExpectedVolumes: monthlyVolumes?.map(data => ({
      leftText: data.name,
      value: data.reference,
    })),
  }

  const comboboxGroupItems: Partial<Record<BusinessCustomerFormKey, ComboboxGroupItem[]>> = {
    industry: industryOptions,
  }

  const isComboboxLoading: Partial<Record<BusinessCustomerFormKey, boolean>> = {
    countryOfIncorporation: isFetchingAllowedCountries,
    monthlyExpectedVolumes: isFetchingMonthlyVolumes,
    industry: isFetchingIndustries,
  }

  const isErrorLoadingOptions: Partial<Record<BusinessCustomerFormKey, boolean>> = {
    countryOfIncorporation: isErrorFetchingAllowedCountries,
    industry: isErrorFetchingIndustries,
    monthlyExpectedVolumes: isErrorFetchingMonthlyVolumes,
  }

  const optionErrorFields = Object.keys(isErrorLoadingOptions)?.filter(
    key => isErrorLoadingOptions?.[key]
  )

  const effectiveDefaultValues: BusinessCustomerFormData = (() => {
    if (isReliance) return defaultValues
    const {
      taxNumber: _taxNumber,
      registrationNumber: _registrationNumber,
      ...rest
    } = defaultValues
    return rest
  })()

  const formKeys = Object.keys(effectiveDefaultValues) as BusinessCustomerFormKey[]

  const getFieldSchema = (key: BusinessCustomerFormKey): AnySchema => {
    const label = t(
      BUSINESS_CUSTOMER_LABEL_MAPPING?.[key] ?? `embeddedCustomers.businessForm.${key}`
    )
    const fieldSchema = getRequiredYupField({ label })
    switch (key) {
      case 'businessDescription':
        return fieldSchema?.max(FieldMaxLength.LONG, t('formFields.errors.fieldTooLong'))
      case 'externalReference':
        return getYupField({ label })
      case 'taxNumber':
        if (!isReliance) return getYupField({ label })
        return getConditionalYupField<BusinessCustomerFormKey>({
          label,
          fieldKey: 'countryOfIncorporation',
          fieldValue: CountryCodes.US,
        })
      case 'registrationNumber':
        if (!isReliance) return getYupField({ label })
        return getConditionalYupField<BusinessCustomerFormKey>({
          label,
          fieldKey: 'countryOfIncorporation',
          fieldValue: value => !!value && value !== CountryCodes.US,
        })
      default:
        return fieldSchema
    }
  }

  const formSchema = yupObject(
    formKeys?.reduce((acc, key) => {
      acc[key] = getFieldSchema(key)
      return acc
    }, {} as BusinessCustomerFormSchema)
  )
  const form = useForm<BusinessCustomerFormData>({
    defaultValues: effectiveDefaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })
  const { handleSubmit, setValue, setError, control, getValues } = form
  const formValues = useWatch({ control })
  const previousCountryRef = useRef(formValues?.countryOfIncorporation)
  const hasErrors = !!Object.keys(form?.formState?.errors)?.length

  const { setInputField, setComboboxField } = useFormFields<BusinessCustomerFormKey>(
    formSchema,
    form,
    formValues
  )

  const getFormField = (key: BusinessCustomerFormKey) => {
    if (isBusinessComboboxKey(key)) {
      const comboboxField: ComboboxProps = {
        ...(setComboboxField({ key, items: comboboxItems?.[key] }) as ComboboxProps),
        loadingInline: isComboboxLoading?.[key],
        defaultValue: formValues?.[key] as string,
      }
      return comboboxField
    }

    if (isBusinessComboboxGroupKey(key)) {
      const comboboxGroupField: ComboboxGroupProps = {
        ...(setComboboxField({ key, items: comboboxGroupItems?.[key] }) as ComboboxGroupProps),
        loadingInline: isComboboxLoading?.[key],
        defaultValue: formValues?.[key] as string,
      }
      return comboboxGroupField
    }

    return {
      ...setInputField({ key }),
      onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
        setValue(key, event?.target?.value as never, { shouldValidate: true }),
    }
  }

  const formFields = formKeys?.reduce((acc, key) => {
    const accRecord = acc as unknown as Record<BusinessCustomerFormKey, unknown>
    accRecord[key] = getFormField(key)
    return acc
  }, {} as BusinessCustomerFormFields)

  const getComboboxLabel = <LabelType extends string>(key: BusinessCustomerFormKey) => {
    return comboboxItems?.[key]?.find(({ value }) => value === formValues?.[key])
      ?.leftText as LabelType
  }

  const displayData: Partial<BusinessCustomerFormData> = {
    industry: getComboboxLabel('industry'),
    countryOfIncorporation: getComboboxLabel('countryOfIncorporation'),
    monthlyExpectedVolumes: getComboboxLabel('monthlyExpectedVolumes'),
    useCase: getComboboxLabel<UseCase>('useCase'),
  }

  useEffect(() => {
    if (!isReliance) return

    const currentCountry = formValues?.countryOfIncorporation
    const previousCountry = previousCountryRef.current

    if (!currentCountry || currentCountry === previousCountry) return

    const fieldToClear = currentCountry === CountryCodes.US ? 'registrationNumber' : 'taxNumber'
    const fieldValue = getValues(fieldToClear)

    if (fieldValue) {
      setValue(fieldToClear, '', { shouldDirty: true, shouldValidate: true })
    }

    previousCountryRef.current = currentCountry
  }, [formValues?.countryOfIncorporation, getValues, isReliance, setValue])

  useEffect(() => {
    if (fieldErrors) {
      setFieldErrors(fieldErrors, mapBusinessErrorKeyToFormKey, setError)
    }

    if (optionErrorFields?.length) {
      optionErrorFields?.forEach(key => {
        setError(key as BusinessCustomerFormKey, {
          type: 'disabled',
          message: t('formFields.errors.loadingOptions'),
        })
      })
    }
  }, [fieldErrors, optionErrorFields])

  return { formFields, displayData, hasErrors, handleSubmit, form, formValues }
}
